import { useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { validateCemFile } from "@/lib/fileUtils";
import { useArtifactPreview, useConfirmArtifacts } from "@/hooks/useProjects";
import { recordArtefactUpload } from "@/lib/evidenceAudit";
import type { ArtifactPreview } from "@/services/projectsApi";
import ArtifactChecklist from "@/components/evidenceGathering/ArtifactChecklist";
import { normalizeArtifactType } from "@/components/evidenceGathering/ArtifactTypeBadge";
import { FileUploadButton, FileDropzone, ErrorBanner, type FileRejection } from "@/components/ingestion";

interface Props {
  projectId: string;
  /** Which upload primitive to render — a compact trigger button for the
   *  top-right action row once artifacts already exist, or a proper dropzone
   *  for the empty-state entry point. */
  presentation?: "button" | "dropzone";
  /** Trigger button styling (presentation="button" only) — an outline button
   *  in the top-right action row once artifacts already exist, or a primary
   *  brand CTA inside the empty state. */
  triggerVariant?: "brand" | "outline";
  triggerLabel?: string;
  /** Extraction finished, with the uids it produced and the workbook they came
   *  from. The parent opens the review step rather than this component, because
   *  this one is unmounted by its own success on a project's first upload — the
   *  arriving artifacts replace the empty state it lives inside. */
  onExtracted?: (uids: string[], file: File) => void;
}

// Pick a workbook, choose what to pull out of it, extract. Reviewing the
// descriptions that follow belongs to ArtifactReviewDialog — see onExtracted.
//
// Self-contained otherwise: owns its file-picking state, the dialog, and the
// confirm mutation. EvidenceGatheringStep mounts exactly one instance at a
// time — the top-right action row when artifacts exist, or inside
// EmptyArtifactState when they don't — so there's never more than one live copy
// of this state and nothing needs lifting.
const formatFileSize = (bytes: number) => {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${Math.round(mb)}MB`;
  return `${Math.max(1, Math.round(bytes / 1024))}KB`;
};

const UploadCemModel = ({
  projectId,
  presentation = "button",
  triggerVariant = "brand",
  triggerLabel = "Upload CEM File",
  onExtracted,
}: Props) => {
  const [validationError, setValidationError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ArtifactPreview | null>(null);
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [selectedUids, setSelectedUids] = useState<string[]>([]);

  const artifactPreview = useArtifactPreview();
  const confirmArtifacts = useConfirmArtifacts();

  const closeDialog = () => {
    setPreview(null);
    setSourceFile(null);
    setSelectedUids([]);
    confirmArtifacts.reset();
  };

  const handleFilesAdmitted = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setPreview(null);
    setSourceFile(file);
    artifactPreview.mutate(
      { projectId, file },
      {
        onSuccess: (data) => {
          setPreview(data);
          // Everything checked by default, minus the Sheet rows — those are
          // group headers, not selectable artifacts.
          setSelectedUids(
            data.artifacts
              .filter((a) => normalizeArtifactType(a.type) !== "Sheet")
              .map((a) => a.uid)
          );
        },
      }
    );
  };

  const handleRejected = (rejections: FileRejection[]) => {
    setValidationError(rejections[0]?.message ?? null);
  };

  const handleExtract = () => {
    if (!preview || !sourceFile) return;
    confirmArtifacts.mutate(
      { projectId, file: sourceFile, selectedUids },
      {
        // Hand the file over before clearing it — the review step needs it to
        // offer a retry, and closeDialog drops the reference.
        onSuccess: (artifacts) => {
          recordArtefactUpload(projectId, preview.sourceFilename || sourceFile.name, artifacts);
          onExtracted?.(
            artifacts.map((a) => a.uid),
            sourceFile
          );
          closeDialog();
        },
      }
    );
  };

  const isConfirming = confirmArtifacts.isPending;

  const pickerProps = {
    accept: ".xlsx,.xlsm,.xlsb",
    validate: validateCemFile,
    onFilesAdmitted: handleFilesAdmitted,
    onRejected: handleRejected,
    isBusy: artifactPreview.isPending,
  };

  return (
    <>
      <ErrorBanner message={validationError} className="mb-4 max-w-md" />
      <ErrorBanner
        message={artifactPreview.isError ? (artifactPreview.error?.message || "Upload failed. Please try again.") : null}
        className="mb-4 max-w-md"
      />

      {presentation === "dropzone" ? (
        <FileDropzone
          {...pickerProps}
          label="Drop your CEM file here or click to browse"
          hint="Supports .xlsx, .xlsm and .xlsb."
        />
      ) : (
        <FileUploadButton {...pickerProps} variant={triggerVariant} idleLabel={triggerLabel} />
      )}

      <Dialog open={!!preview} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="sm:max-w-[1320px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Upload CEM Model</DialogTitle>
            <DialogDescription>Check the list of Artefacts to extract</DialogDescription>
          </DialogHeader>

          <ErrorBanner
            message={confirmArtifacts.isError ? (confirmArtifacts.error?.message || "Extraction failed. Please try again.") : null}
          />

          <div className="flex items-center justify-between rounded-md border px-4 py-3">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-sm font-medium truncate">{preview?.sourceFilename}</span>
              {sourceFile && (
                <span className="text-xs text-muted-foreground shrink-0">({formatFileSize(sourceFile.size)})</span>
              )}
            </div>
            {isConfirming && (
              <span className="flex items-center gap-1.5 text-sm text-brand-teal shrink-0">
                <Loader2 className="w-4 h-4 animate-spin" /> Processing…
              </span>
            )}
          </div>

          {preview && (
            <ArtifactChecklist
              items={preview.artifacts}
              selectedUids={selectedUids}
              onSelectionChange={setSelectedUids}
            />
          )}

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={closeDialog}
              disabled={isConfirming}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="brand"
              onClick={handleExtract}
              disabled={isConfirming || selectedUids.length === 0}
            >
              {isConfirming ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extracting…
                </>
              ) : (
                "Extract Artefacts"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UploadCemModel;
